import api from './api';
import { AuthResponse, User } from '../types';

export const authService = {
  async register(name: string, email: string, password: string): Promise<{ message: string; userId: number }> {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },

  async registerWithFormData(formData: FormData): Promise<{ message: string; userId: number }> {
    const response = await api.post('/auth/register', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    // console.log('Register with FormData API response:', response.data);
    return response.data;
  },

  async login(identifier: string, password: string, loginMethod: 'email' | 'userid' | 'phone' = 'email'): Promise<AuthResponse> {
    try {
      // Determine the login type based on the identifier format
      let loginType = loginMethod;
      
      // Auto-detect login type if not specified
      if (loginType === 'email') {
        if (identifier.includes('@')) {
          loginType = 'email';
        } else if (/^[+]?[\d\s\-\(\)]{10,20}$/.test(identifier)) {
          loginType = 'phone';
        } else {
          loginType = 'userid';
        }
      }

      // Prepare the request payload based on login type
      let payload: any;
      
      switch (loginType) {
        case 'email':
          payload = { email: identifier, password };
          break;
        case 'phone':
          payload = { phone: identifier, password };
          break;
        case 'userid':
          payload = { userid: identifier, password };
          break;
        default:
          payload = { email: identifier, password }; // fallback to email
      }

      console.log('Login request payload:', payload);
      
      const response = await api.post('/auth/login', payload);
      const { token, refreshToken, user } = response.data;
      
      // console.log('Login API response:', response.data);
      
      // Store auth data
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      return response.data;
    } catch (error: any) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config,
        code: error.code,
        identifier,
        loginMethod
      });
      
      // Check if it's a CORS error (network error with no response)
      if (!error.response && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')) {
        console.error('Possible CORS error. Backend may not allow requests from:', window.location.origin);
        throw new Error('Unable to connect to server. This may be due to CORS restrictions. Please contact support.');
      }
      
      // Re-throw the original error for other cases
      throw error;
    }
  },

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    
    try {
      if (refreshToken) {
      await api.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Error during logout API call:', error);
      // Continue with logout process even if API call fails
    } finally {
      // Clear local storage regardless of API success
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    }
  },

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh-token', { refreshToken });
    const { token, refreshToken: newRefreshToken } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);

    return token;
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  },

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token;
  },

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'admin';
  },

  isStudent(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'student';
  }
}; 